/**
 * Pre-game lobby screen.
 * Shown while game_state === 'lobby'.
 * - Host sees the room code, list of joined players, timer picker, and a "START SESSION" button
 * - Players see the room code, joined players, timer setting (read-only), and a "Waiting for host…" message
 */
const TIMER_PRESETS = [
  { value: 0, label: "OFF" },
  { value: 5, label: "5s" },
  { value: 10, label: "10s" },
  { value: 15, label: "15s" },
  { value: 30, label: "30s" },
  { value: 60, label: "60s" },
];

const Lobby = ({
  roomCode,
  participants,
  isHost,
  myName,
  theme,
  roundTimerSeconds,
  mode = "solo",
  teams = [],
  onSetRoundTimer,
  onStartSession,
  onOpenShare,
  onJoinTeam,
  onCreateTeam,
  onRenameTeam,
  onRemoveTeam,
  voiceEnabled = false,
  onSetVoiceEnabled,
}) => {
  // Non-host participants who are currently connected
  const players = participants.filter((p) => !p.is_host && p.socket_id !== null);
  const accent = theme?.accentColor || "#00F0FF";
  const title = theme?.titleColor || "#FFEA00";
  const buzzer = theme?.buzzerColor || "#FF0055";

  const selected = roundTimerSeconds || 0;
  const isTeamsMode = mode === "teams";
  const myParticipant = participants.find((p) => p.name === myName);
  const myTeamId = myParticipant?.team_id || null;
  const unassignedPlayers = players.filter((p) => !p.team_id);
  const canStart = !isTeamsMode || (teams.length >= 2 && unassignedPlayers.length === 0);

  return (
    <div className="w-full max-w-lg mx-auto" data-testid="lobby-screen">
      {/* Header */}
      <div className="text-center mb-6">
        <p
          className="tracking-[0.3em] text-sm font-semibold mb-1"
          style={{ color: accent }}
        >
          {isHost ? "READY TO START" : "WAITING TO PLAY"}
        </p>
        <h1
          className="text-5xl sm:text-6xl text-white mb-2"
          style={{ fontFamily: "Anton, sans-serif" }}
        >
          LOBBY
        </h1>
        <div className="inline-block bg-black/50 border border-white/10 rounded-2xl px-5 py-3 mt-2 max-w-full">
          <p className="text-xs text-[#A1A1AA] uppercase tracking-widest">Room Code</p>
          <p
            className={`${
              (roomCode || "").length <= 8
                ? "text-4xl tracking-wide"
                : (roomCode || "").length <= 16
                ? "text-3xl"
                : (roomCode || "").length <= 32
                ? "text-2xl"
                : "text-lg"
            } break-words line-clamp-2 max-w-full`}
            style={{
              fontFamily: "Anton, sans-serif",
              color: title,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
            title={roomCode}
            data-testid="lobby-room-code"
          >
            {roomCode}
          </p>
        </div>
      </div>

      {/* Player list — hidden in team mode (players are shown inside team cards below) */}
      {!isTeamsMode && (
      <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden mb-5">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3
            className="text-white font-bold tracking-wider"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            PLAYERS
          </h3>
          <span className="text-xs text-[#A1A1AA]">
            {players.length} joined
          </span>
        </div>
        {players.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-2">👥</div>
            <p className="text-[#A1A1AA] text-sm">Waiting for players to join…</p>
            {isHost && (
              <button
                onClick={onOpenShare}
                className="mt-4 px-5 py-2 bg-white/10 border border-white/20 rounded-full text-sm font-semibold hover:bg-white/20 transition-all"
                data-testid="lobby-share-cta"
              >
                📤 Share room link
              </button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {players.map((p, idx) => (
              <li
                key={p.name}
                className={`px-4 py-3 flex items-center gap-3 ${
                  p.name === myName ? "bg-white/5" : ""
                }`}
                data-testid={`lobby-player-${p.name}`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-black"
                  style={{ background: [accent, title, buzzer, "#9D4EDD"][idx % 4] }}
                >
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 text-white font-semibold truncate">
                  {p.name}
                  {p.name === myName && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A1A1AA]">
                      (you)
                    </span>
                  )}
                </div>
                <div className="text-xs text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Ready
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      )}

      {/* Team picker — only in team mode */}
      {isTeamsMode && (
        <div className="mb-5" data-testid="team-picker">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold tracking-wider" style={{ fontFamily: "Anton, sans-serif" }}>
              TEAMS · {teams.length}
            </h3>
            {isHost && teams.length < 6 && (
              <button
                onClick={() => {
                  const name = prompt("Team name?", `Team ${teams.length + 1}`);
                  if (name && name.trim()) onCreateTeam(name.trim());
                }}
                className="px-3 py-1.5 bg-[#00F0FF]/20 border border-[#00F0FF]/40 text-[#00F0FF] rounded-full text-xs font-bold hover:bg-[#00F0FF]/30"
                data-testid="lobby-add-team"
              >
                + ADD TEAM
              </button>
            )}
          </div>

          {unassignedPlayers.length > 0 && (
            <div className="bg-[#FFEA00]/10 border border-[#FFEA00]/40 rounded-xl px-3 py-2 mb-3">
              <p className="text-xs text-[#FFEA00] tracking-widest font-bold mb-1">
                UNASSIGNED · {unassignedPlayers.length}
              </p>
              <div className="flex flex-wrap gap-2">
                {unassignedPlayers.map((p) => (
                  <span
                    key={p.name}
                    className="px-2 py-1 bg-black/40 rounded-full text-xs text-white inline-flex items-center gap-1"
                    data-testid={`lobby-unassigned-${p.name}`}
                  >
                    {p.name}
                    {p.name === myName && <span className="text-[#A1A1AA] ml-1">(you)</span>}
                    {isHost && teams.length > 0 && (
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) onJoinTeam(e.target.value, p.name);
                        }}
                        className="bg-transparent text-white/70 border border-white/20 rounded-full px-1 py-0 text-[10px] cursor-pointer hover:text-white hover:border-white/40 outline-none"
                        title={`Assign ${p.name} to a team`}
                        data-testid={`lobby-assign-${p.name}`}
                      >
                        <option value="" className="text-black">→ Team</option>
                        {teams.map((tt) => (
                          <option key={tt.id} value={tt.id} className="text-black">
                            {tt.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-[#A1A1AA] mt-2">
                {isHost
                  ? "Assign each player from the dropdown, or ask them to click a team below."
                  : "Click a team below to join."}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {teams.map((t) => {
              const members = players.filter((p) => p.team_id === t.id);
              const isMine = myTeamId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onJoinTeam(t.id)}
                  className={`text-left rounded-2xl border-2 p-4 transition-all active:scale-[0.98] ${
                    isMine ? "shadow-[0_0_20px] scale-[1.01]" : "hover:scale-[1.01]"
                  }`}
                  style={{
                    borderColor: t.color,
                    background: `${t.color}11`,
                    boxShadow: isMine ? `0 0 20px ${t.color}66` : undefined,
                  }}
                  data-testid={`lobby-team-${t.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="text-lg font-bold truncate"
                        style={{ color: t.color, fontFamily: "Anton, sans-serif" }}
                      >
                        {t.name}
                      </span>
                      {isMine && (
                        <span className="text-[10px] uppercase tracking-widest text-white/60">
                          (yours)
                        </span>
                      )}
                    </div>
                    {isHost && (
                      <div className="flex gap-1 shrink-0">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            const n = prompt("Rename team", t.name);
                            if (n && n.trim()) onRenameTeam(t.id, n.trim());
                          }}
                          className="text-xs text-white/50 hover:text-white cursor-pointer"
                          data-testid={`lobby-rename-${t.id}`}
                        >
                          ✎
                        </span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Remove ${t.name}?`)) onRemoveTeam(t.id);
                          }}
                          className="text-xs text-white/50 hover:text-[#FF0055] cursor-pointer"
                          data-testid={`lobby-remove-${t.id}`}
                        >
                          ✕
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-white/60 mb-1">
                    {members.length} member{members.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {members.map((m) => (
                      <span
                        key={m.name}
                        onClick={(e) => e.stopPropagation()}
                        className="px-2 py-0.5 bg-black/40 rounded-full text-xs text-white inline-flex items-center gap-1"
                      >
                        {m.name}
                        {m.name === myName && <span className="text-white/50 ml-1">(you)</span>}
                        {/* Host: swap this player to another team (or unassigned) via a compact <select>.
                            Uses a NON-standard "unassigned" sentinel that maps to null on emit. */}
                        {isHost && (
                          <select
                            value={t.id}
                            onChange={(e) => {
                              e.stopPropagation();
                              const v = e.target.value;
                              onJoinTeam(v === "__none__" ? null : v, m.name);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-transparent text-white/70 border border-white/20 rounded-full px-1 py-0 text-[10px] cursor-pointer hover:text-white hover:border-white/40 outline-none"
                            title={`Move ${m.name}`}
                            data-testid={`lobby-move-${m.name}`}
                          >
                            {teams.map((tt) => (
                              <option key={tt.id} value={tt.id} className="text-black">
                                {tt.name}
                              </option>
                            ))}
                            <option value="__none__" className="text-black">Unassigned</option>
                          </select>
                        )}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          {teams.length < 2 && isHost && (
            <p className="text-xs text-[#FF0055] text-center mt-3">
              You need at least 2 teams to start.
            </p>
          )}
        </div>
      )}

      {/* Answer timer picker — time each buzzer-winner has to answer */}
      <div
        className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-4 mb-5"
        data-testid="round-timer-picker"
      >
        <div className="flex items-center justify-between mb-3">
          <h3
            className="text-white font-bold tracking-wider text-sm"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            ⏱ TIME TO ANSWER
          </h3>
          <span className="text-xs text-[#A1A1AA]">
            {selected === 0 ? "No time limit" : `${selected}s after buzz`}
          </span>
        </div>
        {isHost ? (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {TIMER_PRESETS.map(({ value, label }) => {
                const isSelected = selected === value;
                return (
                  <button
                    key={value}
                    onClick={() => onSetRoundTimer(value)}
                    className={`py-2 rounded-xl text-sm font-bold tracking-wide transition-all active:scale-95 border-2 ${
                      isSelected
                        ? "text-black"
                        : "bg-white/5 border-white/10 text-white hover:bg-white/10"
                    }`}
                    style={
                      isSelected
                        ? {
                            background: accent,
                            borderColor: accent,
                            boxShadow: `0 0 15px ${accent}66`,
                          }
                        : undefined
                    }
                    data-testid={`timer-preset-${value}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#A1A1AA] mt-3 leading-relaxed">
              {selected === 0
                ? "The player who buzzes in gets unlimited time to answer."
                : `The player who buzzes in gets ${selected} seconds to answer. If time runs out, they're locked out for the round and buzzers reopen for everyone else.`}
            </p>
          </>
        ) : (
          <p className="text-[13px] text-[#A1A1AA]">
            {selected === 0
              ? "You'll have unlimited time to answer after buzzing in."
              : `You'll have ${selected} seconds to answer after buzzing in.`}
          </p>
        )}
      </div>

      {/* Mics on/off — host can flip this any time before start; fixed once the game begins */}
      <div
        className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-4 mb-5"
        data-testid="voice-picker"
      >
        <div className="flex items-center justify-between mb-3">
          <h3
            className="text-white font-bold tracking-wider text-sm"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            🎙️ VOICE CHAT
          </h3>
          <span className="text-xs text-[#A1A1AA]">
            {voiceEnabled ? "Mics on" : "Mics off"}
          </span>
        </div>
        {isHost ? (
          <>
            <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
              <button
                type="button"
                onClick={() => onSetVoiceEnabled(false)}
                className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                  !voiceEnabled
                    ? "bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                    : "text-[#A1A1AA] hover:text-white"
                }`}
                data-testid="voice-off-lobby"
              >
                🔇 NO MICS
              </button>
              <button
                type="button"
                onClick={() => onSetVoiceEnabled(true)}
                className={`flex-1 py-2 rounded-lg font-semibold tracking-wide transition-all text-sm ${
                  voiceEnabled
                    ? "bg-[#9D4EDD] text-black shadow-[0_0_15px_rgba(157,78,221,0.5)]"
                    : "text-[#A1A1AA] hover:text-white"
                }`}
                data-testid="voice-on-lobby"
              >
                🎙️ WITH MICS
              </button>
            </div>
            <p className="text-[11px] text-[#A1A1AA] mt-3 leading-relaxed">
              {voiceEnabled
                ? "Everyone gets a mic — good when players aren't all in the same room. You'll be asked for mic access now, before the game starts."
                : "Buzzer only — switch to “with mics” if some players are joining remotely."}
            </p>
          </>
        ) : (
          <p className="text-[13px] text-[#A1A1AA]">
            {voiceEnabled
              ? "The host turned on mics — you'll be asked for mic access shortly."
              : "No mics for this game — buzzer only."}
          </p>
        )}
      </div>

      {/* Host: start button */}
      {isHost && (
        <>
          <button
            onClick={onStartSession}
            disabled={players.length === 0 || !canStart}
            className="w-full py-5 rounded-2xl text-black text-2xl font-bold tracking-wide transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: accent,
              boxShadow: players.length > 0 && canStart ? `0 0 30px ${accent}80` : "none",
              fontFamily: "Anton, sans-serif",
            }}
            data-testid="start-session-button"
          >
            ▶ START SESSION
          </button>
          {players.length === 0 && (
            <p className="text-center text-[#A1A1AA] text-xs mt-3">
              Invite at least one player to start
            </p>
          )}
          {players.length > 0 && !canStart && isTeamsMode && (
            <p className="text-center text-[#FFEA00] text-xs mt-3">
              {teams.length < 2
                ? "Add at least 2 teams to start"
                : "Every player must pick a team first"}
            </p>
          )}
          {players.length > 0 && canStart && (
            <p className="text-center text-[#A1A1AA] text-xs mt-3">
              You can invite more players even after starting
            </p>
          )}
        </>
      )}

      {/* Player: waiting message */}
      {!isHost && (
        <div className="text-center bg-black/30 border border-white/10 rounded-2xl px-5 py-6">
          <div className="text-3xl mb-2">⏳</div>
          <p
            className="text-white text-lg font-semibold mb-1"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            WAITING FOR HOST
          </p>
          <p className="text-[#A1A1AA] text-sm">
            The game will start any second now. Keep this screen open.
          </p>
        </div>
      )}
    </div>
  );
};

export default Lobby;
